import { useState, useEffect } from "react";
import { getDashboard } from "@/lib/api";
import StatCard from "@/components/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Activity, 
  Droplets, 
  Bug, 
  HandMetal, 
  ShieldCheck, 
  Users,
  TrendingUp,
  AlertTriangle,
  Syringe,
  Stethoscope,
  Heart,
  Building2
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  Area,
  ComposedChart
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getDashboard(year);
        setData(result);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year]);

  // Fetch logo
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/logo`);
        const data = await res.json();
        if (data.exists && data.data) {
          setLogoUrl(`data:${data.content_type};base64,${data.data}`);
        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
      }
    };
    fetchLogo();
  }, []);

  const years = [2024, 2025, 2026, 2027];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const goals = data?.goals || {};
  const ytdRates = data?.ytd_rates || {};
  const ytdStats = data?.ytd_stats || {};
  const monthlyTrend = data?.monthly_trend || [];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt="Organization Logo" 
              className="h-14 w-auto max-w-[180px] object-contain"
              data-testid="dashboard-logo"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#6A26B5] to-[#5a1f9e] flex items-center justify-center">
              <Heart className="w-8 h-8 text-white" fill="white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Infection Control Scorecard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Year-to-Date Summary • {data?.months_with_data || 0} month(s) of data
            </p>
          </div>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-32 border-[#6A26B5]/30 focus:ring-[#6A26B5]" data-testid="year-selector">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Patient Days & Admissions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Patient Days"
          value={ytdStats.patient_days}
          icon={Users}
        />
        <StatCard
          title="Admissions"
          value={ytdStats.admissions}
          icon={Activity}
        />
        <StatCard
          title="Catheter Days"
          value={ytdStats.urinary_catheter_days}
          icon={Droplets}
        />
        <StatCard
          title="Central Line Days"
          value={ytdStats.central_line_days}
          icon={Syringe}
        />
      </div>

      {/* HAI Section Header */}
      <div className="flex items-center gap-2 pt-4">
        <Bug className="w-5 h-5 text-[#6A26B5]" />
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Hospital Acquired Infection Rates
        </h2>
      </div>

      {/* Infection Rates */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="CAUTI Rate"
          value={ytdRates.cauti_rate}
          unit="/1K cath days"
          goal={goals.cauti_rate}
          icon={Droplets}
        />
        <StatCard
          title="CLABSI Rate"
          value={ytdRates.clabsi_rate}
          unit="/1K line days"
          goal={goals.clabsi_rate}
          icon={Syringe}
        />
        <StatCard
          title="C.diff Rate"
          value={ytdRates.cdiff_rate}
          unit="/10K pt days"
          goal={goals.cdiff_rate}
          icon={Bug}
        />
        <StatCard
          title="Total HAI Rate"
          value={ytdRates.total_hai_rate}
          unit="/1K pt days"
          goal={goals.total_hai_rate}
          icon={AlertTriangle}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="SUTI Rate"
          value={ytdRates.suti_rate}
          unit="/1K pt days"
          goal={goals.suti_rate}
        />
        <StatCard
          title="MDRO Rate"
          value={ytdRates.mdro_rate}
          unit="/1K pt days"
          goal={goals.mdro_rate}
        />
        <StatCard
          title="Pneumonia Rate"
          value={ytdRates.pneumonia_rate}
          unit="/1K pt days"
          goal={goals.pneumonia_rate}
        />
        <StatCard
          title="Primary BSI Rate"
          value={ytdRates.primary_bsi_rate}
          unit="/1K pt days"
          goal={goals.primary_bsi_rate}
        />
      </div>

      {/* Compliance Section */}
      <div className="flex items-center gap-2 pt-4">
        <ShieldCheck className="w-5 h-5 text-[#6A26B5]" />
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Compliance Metrics
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Hand Hygiene"
          value={ytdRates.hand_hygiene_rate}
          unit="%"
          goal={goals.hand_hygiene_compliance}
          inverse={true}
          icon={HandMetal}
        />
        <StatCard
          title="Isolation PPE"
          value={ytdRates.isolation_ppe_rate}
          unit="%"
          goal={goals.isolation_ppe_compliance}
          inverse={true}
          icon={ShieldCheck}
        />
        <StatCard
          title="IC EOC Rounds"
          value={ytdRates.ic_eoc_rate}
          unit="%"
          goal={goals.ic_eoc_compliance}
          inverse={true}
          icon={Stethoscope}
        />
        <StatCard
          title="Dialysis EOC"
          value={ytdRates.dialysis_eoc_rate}
          unit="%"
          goal={goals.dialysis_eoc_compliance}
          inverse={true}
          icon={Activity}
        />
      </div>

      {/* Device Utilization */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          title="Urinary Catheter Utilization"
          value={ytdRates.urinary_catheter_utilization}
          goal={goals.urinary_catheter_utilization}
          icon={Droplets}
        />
        <StatCard
          title="Central Line Utilization"
          value={ytdRates.central_line_utilization}
          goal={goals.central_line_utilization}
          icon={Syringe}
        />
      </div>

      {/* Trend Charts */}
      {monthlyTrend.length > 0 && (
        <>
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <TrendingUp className="w-4 h-4 text-[#6A26B5]" />
                Infection Rate Trends
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="total_hai_rate" name="Total HAI" fill="#6A26B5" fillOpacity={0.1} stroke="#6A26B5" strokeWidth={2} />
                  <Line type="monotone" dataKey="cauti_rate" name="CAUTI" stroke="#00A0B0" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="clabsi_rate" name="CLABSI" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <HandMetal className="w-4 h-4 text-[#6A26B5]" />
                Compliance Trends
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="hand_hygiene_rate" name="Hand Hygiene %" fill="#6A26B5" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="isolation_ppe_rate" name="Isolation PPE %" fill="#00A0B0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bloodborne Pathogen Exposures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-[#f5f0fa] rounded-lg">
                <p className="text-2xl font-bold text-[#6A26B5] font-mono">{ytdStats.sticks || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Sticks</p>
              </div>
              <div className="p-4 bg-[#f5f0fa] rounded-lg">
                <p className="text-2xl font-bold text-[#6A26B5] font-mono">{ytdStats.sharps || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Sharps</p>
              </div>
              <div className="p-4 bg-[#f5f0fa] rounded-lg">
                <p className="text-2xl font-bold text-[#6A26B5] font-mono">{ytdStats.splashes || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Splashes</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-gradient-to-r from-[#6A26B5] to-[#5a1f9e] rounded-lg text-center">
              <p className="text-sm font-medium text-white">
                Total Exposures: <span className="font-bold font-mono">{ytdRates.total_exposures || 0}</span>
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Employee Call-ins (Communicable Illness)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-4 bg-[#f5f0fa] rounded-lg">
                <p className="text-2xl font-bold text-[#6A26B5] font-mono">{ytdStats.respiratory_callins || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Respiratory</p>
              </div>
              <div className="p-4 bg-[#f5f0fa] rounded-lg">
                <p className="text-2xl font-bold text-[#6A26B5] font-mono">{ytdStats.gi_callins || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Gastrointestinal</p>
              </div>
              <div className="p-4 bg-[#f5f0fa] rounded-lg">
                <p className="text-2xl font-bold text-[#6A26B5] font-mono">{ytdStats.skin_callins || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Skin</p>
              </div>
              <div className="p-4 bg-[#f5f0fa] rounded-lg">
                <p className="text-2xl font-bold text-[#6A26B5] font-mono">{ytdStats.other_callins || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Other</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-gradient-to-r from-[#6A26B5] to-[#5a1f9e] rounded-lg text-center">
              <p className="text-sm font-medium text-white">
                Total Call-ins: <span className="font-bold font-mono">{ytdRates.total_callins || 0}</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* HAI Counts Summary */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Year-to-Date Infection Counts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {[
              { label: "CAUTI", value: ytdStats.cauti_count },
              { label: "CLABSI", value: ytdStats.clabsi_count },
              { label: "C.diff", value: ytdStats.cdiff_count },
              { label: "MDRO", value: ytdStats.mdro_count },
              { label: "Pneumonia", value: ytdStats.pneumonia_count },
              { label: "SUTI", value: ytdStats.suti_count },
              { label: "ABUTI", value: ytdStats.abuti_count },
              { label: "Primary BSI", value: ytdStats.primary_bsi_count },
              { label: "Skin/Soft Tissue", value: ytdStats.skin_soft_tissue_count },
              { label: "Total HAIs", value: ytdRates.total_hai_count },
            ].map((item) => (
              <div key={item.label} className="p-3 bg-[#f5f0fa] rounded-lg text-center">
                <p className="text-xl font-bold text-[#6A26B5] font-mono">{item.value || 0}</p>
                <p className="text-xs text-slate-500 mt-1">{item.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
